import { FC, useState, useEffect } from "react";
import TableComponent from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import mockEmployees from "../../mockEmloyees.json";              
import { setIntoLocalStorage, getFromLocalStorage } from "../../lib/utils";
import ModalError from "../ModalError";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { useTheme } from "@mui/material/styles";
import Filter from "./ui/Filter";

const tableHeaders = [
  { id: "Surname", label: "Фамилия", minWidth: 70 },
  { id: "Name", label: "Имя", minWidth: 70 },
  { id: "Patronymic", label: "Отчество", minWidth: 70 },
  { id: "EmploymentDate", label: "Дата приёма", minWidth: 70 },
  { id: "Section", label: "Отделение", minWidth: 70 },
  { id: "WorkPosition", label: "Должность", minWidth: 70 },
  { id: "DismissDate", label: "Дата увольнения", minWidth: 70 },
  { id: "WorkType", label: "Вид работы", minWidth: 70 },
];

function TablePaginationActions(props: {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number
  ) => void;
}) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

const Table: FC<{
  selectedEmployees: {
    Id: string;
    Name: string;
    Surname: string;
    Patronymic: string;
    EmploymentDate: string;
    Section: string;
    WorkPosition: string;
    DismissDate: string;
    WorkType: string;
  }[];
  setSelectedEmployees: (
    value: React.SetStateAction<
      {
        Id: string;
        Name: string;
        Surname: string;
        Patronymic: string;
        EmploymentDate: string;
        Section: string;
        WorkPosition: string;
        DismissDate: string;
        WorkType: string;
      }[]
    >
  ) => void;
}> = ({ selectedEmployees, setSelectedEmployees }) => {
  const [page, setPage] = useState<number>(
    getFromLocalStorage("page") ? Number(getFromLocalStorage("page")) : 0
  );
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    getFromLocalStorage("rowsPerPage")
      ? Number(getFromLocalStorage("rowsPerPage"))
      : 10
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleFilterData = (value: {
    surname: string;
    name: string;
    patronymic: string;
    section: string;
    workPosition: string;
  }) => {
    console.log("handleFilterData", value);
  };

  const handleEmployeSelect = (employe: {
    Id: string;
    Name: string;
    Surname: string;
    Patronymic: string;
    EmploymentDate: string;
    Section: string;
    WorkPosition: string;
    DismissDate: string;
    WorkType: string;
  }) => {
    if (selectedEmployees.length > 4) {
      setIsModalOpen(true);
      return;
    }
    if (!selectedEmployees.includes(employe)) {
      setSelectedEmployees([...selectedEmployees, employe]);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleModalClose = () => setIsModalOpen(false);

  useEffect(() => {
    setIntoLocalStorage("page", page);
  }, [page]);

  useEffect(() => {
    setIntoLocalStorage("rowsPerPage", rowsPerPage);
  }, [rowsPerPage]);

  return (
    <>
      <Filter handleFilterData={handleFilterData} />
      <TableContainer>
        <TableComponent stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {tableHeaders.map((column) => (
                <TableCell key={column.id} size="small">
                  <b>{column.label}</b>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {mockEmployees.testEmployees
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => {
                return (
                  <TableRow hover role="checkbox" tabIndex={-1} key={row.Id}>
                    {tableHeaders.map((column) => (
                      <TableCell
                        key={column.id}
                        onClick={() => handleEmployeSelect(row)}
                        size="small"
                      >
                        {/* @ts-ignore */}
                        {row[column.id]}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
          </TableBody>
        </TableComponent>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={mockEmployees.testEmployees.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ActionsComponent={TablePaginationActions}
      />
      <ModalError open={isModalOpen} onClose={handleModalClose} />
    </>
  );
};

export default Table;
